// ── Adapter Supabase do ILeadRepository ───────────────────────────
// Implementa a porta do módulo Leads usando o cliente Supabase. É a ÚNICA
// camada que conhece as RPCs do banco.

import { getSupabase } from '@infra/supabase/client';
import type { Database } from '@/types/database.types';
import type { ILeadRepository, CriarLeadParams, CreateLeadDirectParams, LeadGraphVisibility } from '@modules/leads';
import type {
  LeadWithGraph, LeadCardGraph, LeadTagGraph,
  LeadDetail, LeadSearchResult, LeadUpdateInput, MobileLeadItem, MobileLeadsPage,
} from '@modules/leads';

type RpcName = keyof Database['public']['Functions'];
type LeadUpdate = Database['public']['Tables']['leads']['Update'];
type LeadStatus = Database['public']['Tables']['leads']['Row']['status'];

// Shape cru do grafo antes da normalização (cards incluem desativados; tags vêm
// aninhadas em lead_tags). A normalização é responsabilidade DESTA camada.
interface RawCard extends LeadCardGraph { ativo: boolean | null; status: string }
interface RawLeadTag { tags: LeadTagGraph | null }
interface RawLead extends Record<string, unknown> {
  id: string;
  cards?: RawCard[] | null;
  lead_tags?: RawLeadTag[] | null;
}

// Filtra cards desativados (o join PostgREST não impõe ativo=true) e eleva
// lead_tags → tags. EXCEÇÃO: cards PERDIDOS (status='lost') seguem para o front.
const normalizeGraph = (data: RawLead[] | null): LeadWithGraph[] =>
  (data || []).map(l => ({
    ...l,
    cards: (l.cards || []).filter(c => c.ativo !== false || c.status === 'lost'),
    tags: (l.lead_tags || []).map(lt => lt.tags).filter((t): t is LeadTagGraph => !!t && t.ativo !== false),
  })) as unknown as LeadWithGraph[];

// Grafo completo do lead (cards/tags/meetings/responsáveis) — usado no board.
// Colunas explícitas (não `*`) para não ler do heap campos que NENHUMA tela do
// board consome: os de atribuição de marketing (utm_*, publico, campanha,
// criativo, merged_into) só aparecem no modal de detalhe, que
// usa SELECT_RICH. Isso reduz o volume lido por leitura em cargas grandes.
// ⚠️ Ao usar uma coluna nova de `leads` numa tela do board, adicione-a aqui.
const LEADS_GRAPH_SELECT = `
  id, workspace_id, nome, email, email_secundario, telefone,
  cargo, area_trabalho, renda_liquida, renda_liquida_value,
  patrimonio, patrimonio_value, proposta, valor_investido, origem, sub_origem, canal,
  status, sdr_id, closer_id, empresa_id, ativo, observacoes, created_at, updated_at, last_activity_at,
  empresa_hoje, principal_desafio, vendedor_nome, vendedor_telefone,
  sdr:profiles!leads_sdr_id_fkey (id, nome, initials, color, avatar_url),
  closer:profiles!leads_closer_id_fkey (id, nome, initials, color, avatar_url),
  empresa:empresas!leads_empresa_id_fkey ( id, nome, logo_url ),
  cards (
    id, stage_id, pipeline_id, order_index, sdr_id, closer_id,
    confirmed, reagend, status, faturamento, is_turbo, ativo,
    won_at, lost_at, loss_reason, linked_card_id,
    pipeline_stages (id, name, color, order_index, is_empresa_gate),
    pipelines (id, name, role, funnel_id, funnel:funnels (id, name))
  ),
  meetings ( id, status, scheduled_at, meeting_type, card_id ),
  lead_tags ( tag_id, tags ( id, name, color, ativo ) )
`;

const requireSupabase = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

// Lead com joins (sdr/closer/tags/cards) — usado na tela de detalhe/modal.
const SELECT_RICH = `
  *,
  sdr:profiles!leads_sdr_id_fkey (id, nome, initials, color, avatar_url),
  closer:profiles!leads_closer_id_fkey (id, nome, initials, color, avatar_url),
  empresa:empresas!leads_empresa_id_fkey ( id, nome, logo_url ),
  lead_tags ( tag_id, tags ( id, name, color, ativo ) ),
  cards (
    id,
    stage_id,
    pipeline_id,
    ativo,
    lost_at,
    status,
    pipeline_stages (id, name, color, order_index, is_empresa_gate),
    pipelines (id, name, role, loss_reasons, funnel:funnel_id ( id, name ))
  )
`;

interface RpcResultShape {
  success?: boolean;
  error?: string;
}

const callRpc = async <T = unknown>(fnName: RpcName, params: Record<string, unknown>): Promise<T> => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase nao inicializado.');
  const { data, error } = await supabase.rpc(fnName, params as never);
  if (error) throw new Error(error.message || `Erro ao chamar ${fnName}`);
  const result = data as RpcResultShape | null;
  if (result && result.success === false) {
    throw new Error(result.error || `Erro ao chamar ${fnName}`);
  }
  return data as T;
};

const buildLeadParams = (params: CriarLeadParams): Record<string, unknown> => ({
  p_workspace_slug: params.workspace_slug,
  p_nome: params.nome,
  p_funnel_nome: params.funnel_nome ?? null,
  p_stage_nome: params.stage_nome ?? null,
  p_email: params.email ?? null,
  p_telefone: params.telefone ?? null,
  p_cargo: params.cargo ?? null,
  p_canal: params.canal ?? null,
  p_origem: params.origem ?? null,
  p_sub_origem: params.sub_origem ?? null,
  p_observacoes: params.observacoes ?? null,
  p_email_secundario: params.email_secundario ?? null,
  p_area_trabalho: params.area_trabalho ?? null,
  p_renda_liquida: params.renda_liquida ?? null,
  p_renda_liquida_value: params.renda_liquida_value ?? null,
  p_patrimonio: params.patrimonio ?? null,
  p_patrimonio_value: params.patrimonio_value ?? null,
  p_valor_investido: params.valor_investido ?? null,
  p_campanha: params.campanha ?? null,
  p_publico: params.publico ?? null,
  p_criativo: params.criativo ?? null,
  p_utm_source:   params.utm_source   ?? null,
  p_utm_medium:   params.utm_medium   ?? null,
  p_utm_campaign: params.utm_campaign ?? null,
  p_utm_content:  params.utm_content  ?? null,
  p_status:       params.status       ?? null,
  p_tags:         params.tags         ?? null,
  p_sdr_email:    params.sdr_email    ?? null,
  p_closer_email: params.closer_email ?? null,
});

export class SupabaseLeadRepository implements ILeadRepository {
  createExternalLead = (params: CriarLeadParams): Promise<unknown> =>
    callRpc('criar_lead_externo', buildLeadParams(params));

  importLead = (params: CriarLeadParams): Promise<unknown> =>
    callRpc('importar_lead' as RpcName, {
      ...buildLeadParams(params),
      p_status: params.status ?? null,
    });

  createFunnel = (workspaceSlug: string, nome: string, descricao: string | null): Promise<unknown> =>
    callRpc('criar_funil', {
      p_workspace_slug: workspaceSlug,
      p_nome: nome,
      p_descricao: descricao,
    });

  getLeadDetail = async (leadId: string): Promise<LeadDetail | null> => {
    const { data, error } = await requireSupabase()
      .from('leads')
      .select(SELECT_RICH)
      .eq('id', leadId)
      .single();
    if (error) throw error;
    return (data ?? null) as unknown as LeadDetail | null;
  };

  updateLead = async (leadId: string, updates: LeadUpdateInput): Promise<LeadDetail | null> => {
    const { data, error } = await requireSupabase()
      .from('leads')
      .update(updates as unknown as LeadUpdate)
      .eq('id', leadId)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return (data ?? null) as unknown as LeadDetail | null;
  };

  patchLead = async (leadId: string, updates: LeadUpdateInput): Promise<void> => {
    const { error } = await requireSupabase()
      .from('leads')
      .update(updates as unknown as LeadUpdate)
      .eq('id', leadId);
    if (error) throw error;
  };

  // Duas queries para qualquer tamanho de lista. Os cards inativos ficam de
  // fora: o histórico de quem trabalhou o card não deve mudar retroativamente.
  assignLeadsOwner = async (leadIds: string[], sdrId: string | null): Promise<void> => {
    if (!leadIds.length) return;
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('leads')
      .update({ sdr_id: sdrId } as unknown as LeadUpdate)
      .in('id', leadIds);
    if (error) throw error;
    const { error: cardError } = await supabase
      .from('cards')
      .update({ sdr_id: sdrId })
      .in('lead_id', leadIds)
      .eq('ativo', true);
    if (cardError) throw cardError;
  };

  deactivateLead = async (leadId: string): Promise<void> => {
    const { error } = await requireSupabase()
      .from('leads')
      .update({ ativo: false })
      .eq('id', leadId);
    if (error) throw error;
  };

  searchLeads = async (workspaceId: string, query: string): Promise<LeadSearchResult[]> => {
    const digits = query.replace(/\D/g, '');
    const filters = [`nome.ilike.%${query}%`];
    if (digits.length >= 3) filters.push(`telefone.ilike.%${digits}%`);
    const { data, error } = await requireSupabase()
      .from('leads')
      .select('id, nome, telefone, email')
      .eq('workspace_id', workspaceId)
      .or(filters.join(','))
      .order('nome')
      .limit(20);
    if (error) throw error;
    return (data || []) as unknown as LeadSearchResult[];
  };

  listMobileMyLeads = async (
    workspaceId: string,
    pipelineId: string,
    search: string,
    limit: number,
    offset: number,
  ): Promise<MobileLeadsPage> => {
    const data = await callRpc<Array<MobileLeadItem & { total_count: number | string }>>('list_mobile_my_leads', {
      p_workspace_id: workspaceId,
      p_pipeline_id: pipelineId,
      p_search: search,
      p_limit: limit,
      p_offset: offset,
    });
    const rows = data || [];
    return {
      items: rows.map(({ total_count, ...lead }) => lead),
      total: Number(rows[0]?.total_count || 0),
    };
  };

  // Teto de linhas do PostgREST. Uma consulta sem `range` é CORTADA aqui, em
  // silêncio: sem erro, sem aviso, sem indicação de que faltou coisa.
  private static readonly GRAPH_PAGE = 1000;
  // Trava de segurança: 50k leads já seria muito além do que o Kanban aguenta
  // renderizar. Existe para a paginação nunca virar laço infinito.
  private static readonly GRAPH_MAX_PAGES = 50;

  // Quantos ids cabem num `in.(...)` sem estourar o tamanho da URL.
  private static readonly ID_CHUNK = 150;

  // Ids dos leads que têm card nos pipelines pedidos. NÃO filtra por card ativo:
  // o Kanban tem a visão "Somente perdidos", e recortar por ativo esvaziaria ela.
  // Leads excluídos já ficam de fora depois, no `ativo = true` da consulta do
  // grafo — card de lead apagado nunca chega à tela.
  private leadIdsInPipelines = async (pipelineIds: string[]): Promise<string[]> => {
    const PAGE = SupabaseLeadRepository.GRAPH_PAGE;
    const ids = new Set<string>();
    for (let page = 0; page < SupabaseLeadRepository.GRAPH_MAX_PAGES; page++) {
      const { data, error } = await requireSupabase()
        .from('cards')
        .select('lead_id')
        .in('pipeline_id', pipelineIds)
        .order('lead_id', { ascending: true })
        .range(page * PAGE, page * PAGE + PAGE - 1);
      if (error) throw error;
      const batch = data ?? [];
      batch.forEach(row => { if (row.lead_id) ids.add(row.lead_id as string); });
      if (batch.length < PAGE) break;
    }
    return [...ids];
  };

  listLeadsGraph = async (
    workspaceId: string,
    visibility?: LeadGraphVisibility | null,
    pipelineIds?: string[] | null,
  ): Promise<LeadWithGraph[]> => {
    // Paginado de propósito. Sem isto a tela recebia só os 1000 leads mais
    // recentes e o Kanban desenhava o board a partir desse recorte — então cada
    // importação de lista empurrava o pipeline real para fora da janela e os
    // cards "sumiam". No TechFinance, com 2307 leads ativos, duas listas
    // ocupavam 991 das 1000 vagas e o board do Perpetuo exibia 16 de 85 cards.
    //
    // `pipelineIds` é o recorte do Kanban, que desenha um pipeline por vez e não
    // tem uso para o workspace inteiro. Os cards embutidos continuam vindo todos
    // (o filtro escolhe QUAIS LEADS buscar, não quais cards embutir), então o
    // pareamento SDR↔Closer e o "passar ao Closer" seguem funcionando.
    const PAGE = SupabaseLeadRepository.GRAPH_PAGE;
    const rows: RawLead[] = [];

    let escopo: string[][] = [[]];
    if (pipelineIds && pipelineIds.length > 0) {
      const ids = await this.leadIdsInPipelines(pipelineIds);
      if (ids.length === 0) return [];
      escopo = [];
      for (let i = 0; i < ids.length; i += SupabaseLeadRepository.ID_CHUNK) {
        escopo.push(ids.slice(i, i + SupabaseLeadRepository.ID_CHUNK));
      }
    }

    for (const lote of escopo) {
      for (let page = 0; page < SupabaseLeadRepository.GRAPH_MAX_PAGES; page++) {
        let q = requireSupabase()
          .from('leads')
          .select(LEADS_GRAPH_SELECT)
          .eq('workspace_id', workspaceId)
          .eq('ativo', true);
        if (lote.length > 0) q = q.in('id', lote);
        // Recorte de visibilidade (Kanban Enterprise, não-gestor): só leads cujo
        // sdr_id OU closer_id é responsável permitido. Defesa em profundidade —
        // a barreira autoritativa contra API direta é a RLS (Config/70).
        if (visibility && visibility.ownerIds.length > 0) {
          const ids = visibility.ownerIds.join(',');
          q = q.or(`sdr_id.in.(${ids}),closer_id.in.(${ids})`);
        }
        const { data, error } = await q
          .order('created_at', { ascending: false })
          // Desempate obrigatório: importação cria centenas de leads no mesmo
          // segundo. Sem uma segunda chave a ordem entre eles não é estável e a
          // paginação repetiria uns e puliria outros.
          .order('id', { ascending: false })
          .range(page * PAGE, page * PAGE + PAGE - 1);
        if (error) throw error;

        const batch = (data ?? []) as unknown as RawLead[];
        rows.push(...batch);
        if (batch.length < PAGE) break;
      }
    }

    return normalizeGraph(rows);
  };

  createLeadWithInitialCard = async (params: CreateLeadDirectParams): Promise<{ lead_id: string }> => {
    const supabase = requireSupabase();
    const { workspaceId, sdrId, closerId } = params;

    const { data: sdrPipeline, error: pipeErr } = await supabase
      .from('pipelines').select('id')
      .eq('workspace_id', workspaceId).eq('role', 'sdr').eq('ativo', true)
      .order('created_at', { ascending: true }).limit(1).maybeSingle();
    if (pipeErr) throw pipeErr;
    if (!sdrPipeline?.id) throw new Error('Pipeline SDR não encontrado para criar o lead.');

    const { data: firstStage, error: stageErr } = await supabase
      .from('pipeline_stages').select('id')
      .eq('pipeline_id', sdrPipeline.id).eq('ativo', true)
      .order('order_index', { ascending: true }).limit(1).maybeSingle();
    if (stageErr) throw stageErr;
    if (!firstStage?.id) throw new Error('Pipeline SDR sem etapas ativas.');

    const { data: lead, error: leadErr } = await supabase
      .from('leads')
      .insert({
        workspace_id: workspaceId,
        nome: params.nome,
        email: params.email || null,
        email_secundario: params.email_secundario || null,
        telefone: params.telefone || null,
        cargo: params.cargo || null,
        area_trabalho: params.area_trabalho || null,
        renda_liquida: params.renda_liquida || null,
        renda_liquida_value: params.renda_liquida_value ?? null,
        patrimonio: params.patrimonio || null,
        patrimonio_value: params.patrimonio_value ?? null,
        valor_investido: params.valor_investido || null,
        canal: params.canal || null,
        origem: params.origem || 'other',
        sub_origem: params.sub_origem || null,
        observacoes: params.observacoes || null,
        status: (params.status || 'new') as LeadStatus,
        sdr_id: sdrId || null,
        closer_id: closerId || null,
      })
      .select('id').single();
    if (leadErr) throw leadErr;

    const { error: rpcCardErr } = await supabase.rpc('create_initial_card_for_lead', {
      p_workspace_id: workspaceId,
      p_lead_id: lead.id,
      p_pipeline_id: sdrPipeline.id,
      p_stage_id: firstStage.id,
      p_sdr_id: sdrId || undefined,
      p_closer_id: closerId || undefined,
    });
    if (rpcCardErr) {
      const missingRpc = /function .*create_initial_card_for_lead|schema cache|could not find/i.test(rpcCardErr.message || '');
      if (!missingRpc) throw rpcCardErr;
      const { error: cardErr } = await supabase.from('cards').insert({
        lead_id: lead.id, pipeline_id: sdrPipeline.id, stage_id: firstStage.id,
        status: 'active', sdr_id: sdrId || null, closer_id: closerId || null, ativo: true,
      });
      if (cardErr) throw cardErr;
    }
    return { lead_id: lead.id };
  };

  passLeadToCloser = async (workspaceId: string, leadId: string, sdrId: string | null): Promise<void> => {
    const supabase = requireSupabase();

    const { data: closerPipeline } = await supabase
      .from('pipelines').select('id')
      .eq('workspace_id', workspaceId).eq('role', 'closer').eq('ativo', true).single();
    if (!closerPipeline) throw new Error('Pipeline Closer não encontrado.');

    const { data: existingCard } = await supabase
      .from('cards').select('id')
      .eq('lead_id', leadId).eq('pipeline_id', closerPipeline.id).single();
    if (existingCard) throw new Error('Lead já foi passado para o Closer.');

    const { data: firstStage } = await supabase
      .from('pipeline_stages').select('id')
      .eq('pipeline_id', closerPipeline.id).eq('ativo', true)
      .order('order_index', { ascending: true }).limit(1).single();
    if (!firstStage) throw new Error('Nenhuma etapa no Pipeline Closer.');

    const { error: cardErr } = await supabase
      .from('cards')
      .insert({ lead_id: leadId, pipeline_id: closerPipeline.id, stage_id: firstStage.id, order_index: 0, sdr_id: sdrId || null, ativo: true });
    if (cardErr) throw cardErr;

    const { error: leadErr } = await supabase.from('leads').update({ status: 'qualified' }).eq('id', leadId);
    if (leadErr) throw leadErr;
  };

  // Precisa ser RPC: `cards_insert` (Config/70 + 88) nega INSERT na pipe de
  // Formalização para os perfis 'sdr' e 'aex' — para eles ela é somente
  // leitura. A `aex_enviar_lead_ao_comercial` é SECURITY DEFINER e abre só
  // esta operação, revalidando workspace, visibilidade do lead e os pipes
  // (ver supabase/migrations/20260806100000_aex_handoff_ao_comercial.sql).
  sendLeadToCloserPipeline: ILeadRepository['sendLeadToCloserPipeline'] = async ({
    leadId, sdrCardId, closerPipelineId,
  }) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase.rpc('aex_enviar_lead_ao_comercial', {
      p_lead_id: leadId,
      p_sdr_card_id: sdrCardId,
      p_closer_pipe_id: closerPipelineId,
    });
    if (error) throw error;
    const result = (data || null) as { card_id?: string | null } | null;
    return { cardId: result?.card_id || null };
  };

  // Também precisa ser RPC, pela mesma razão da `sendLeadToCloserPipeline`
  // acima: o Colaborador não tem INSERT na pipe de Formalização. A
  // `encaminhar_lead_ao_comercial` é SECURITY DEFINER e faz card de origem,
  // card do Comercial, lead e history na mesma transação — ver
  // supabase/migrations/20260922120000_handoff_comercial_sem_reuniao.sql.
  handoffLeadToComercial: ILeadRepository['handoffLeadToComercial'] = async ({
    leadId, sdrCardId, closerId, closerPipelineId, targetStageId = null,
  }) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase.rpc('encaminhar_lead_ao_comercial', {
      p_lead_id: leadId,
      p_sdr_card_id: sdrCardId,
      p_closer_id: closerId,
      p_closer_pipe_id: closerPipelineId,
      // Omitido (e não `null`) quando não há etapa de destino: o argumento
      // tem DEFAULT NULL no SQL e o cliente tipado só aceita string.
      p_target_stage_id: targetStageId ?? undefined,
    });
    if (error) throw error;
    const result = (data || null) as { closer_card_id?: string | null } | null;
    return { closerCardId: result?.closer_card_id || null };
  };
}
